import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  computeProposalTotals,
  type CalculatorState,
  type ProductCatalogItem,
} from "./calculator";
import { formatEur } from "./shared";
import { getProposalTexts, type ProposalLanguage } from "./i18n";

const CALENDLY_URL =
  "https://calendly.com/saravicius/noium-onboarding?hide_event_type_details=1&hide_gdpr_banner=1&primary_color=415cfe";
const CALENDLY_SCRIPT = "https://assets.calendly.com/assets/external/widget.js";

function CalendlyEmbed() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const existing = document.querySelector(
      `script[src="${CALENDLY_SCRIPT}"]`
    ) as HTMLScriptElement | null;
    if (existing) {
      const w = window as unknown as { Calendly?: { initInlineWidget?: (opts: { url: string; parentElement: HTMLElement }) => void } };
      if (w.Calendly?.initInlineWidget && containerRef.current) {
        w.Calendly.initInlineWidget({ url: CALENDLY_URL, parentElement: containerRef.current });
      }
      return;
    }
    const script = document.createElement("script");
    script.src = CALENDLY_SCRIPT;
    script.async = true;
    document.body.appendChild(script);
  }, []);
  return (
    <div
      ref={containerRef}
      className="calendly-inline-widget"
      data-url={CALENDLY_URL}
      style={{ minWidth: "320px", height: "640px" }}
    />
  );
}

export function AcceptProposalDialog({
  open,
  onOpenChange,
  calcState,
  catalog,
  onConfirm,
  isPending,
  isAccepted,
  language,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  calcState: CalculatorState | null;
  catalog: ProductCatalogItem[];
  onConfirm: (input: { requirements?: string }) => void;
  isPending: boolean;
  isAccepted: boolean;
  language: ProposalLanguage;
}) {
  const t = getProposalTexts(language).acceptDialog;
  const calcText = getProposalTexts(language).calculator;
  const totals = calcState ? computeProposalTotals(calcState, catalog) : null;
  const [step, setStep] = useState<1 | 2>(1);
  const [requirements, setRequirements] = useState("");

  useEffect(() => {
    if (open) {
      setStep(isAccepted ? 2 : 1);
      setRequirements("");
    }
  }, [open, isAccepted]);

  useEffect(() => {
    if (isAccepted && open) setStep(2);
  }, [isAccepted, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={
          step === 2
            ? "border-border bg-white sm:max-w-2xl"
            : "border-border bg-white"
        }
      >
        {step === 1 ? (
          <>
            <DialogHeader>
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-brand-blue/15 to-emerald-200/40">
                <Sparkles className="h-6 w-6 text-brand-blue" />
              </div>
              <DialogTitle className="font-display text-2xl font-extrabold leading-tight tracking-display sm:text-3xl">
                {t.step1Title}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                {t.step1Description}
              </DialogDescription>
            </DialogHeader>
            {totals && (
              <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-4 text-sm">
                <div className="flex items-baseline justify-between">
                  <span className="text-muted-foreground">{t.monthlyFee}</span>
                  <span className="font-display text-lg font-bold tabular-nums tracking-display">
                    {formatEur(totals.monthly)}
                    <span className="ml-1 text-xs font-medium text-muted-foreground">
                      {t.perMonth}
                    </span>
                  </span>
                </div>
                {totals.oneTime > 0 && (
                  <div className="flex items-baseline justify-between border-t border-border/60 pt-2">
                    <span className="text-muted-foreground">
                      {calcText.totalOneTime}
                    </span>
                    <span className="font-display text-lg font-bold tabular-nums tracking-display">
                      {formatEur(totals.oneTime)}
                      <span className="ml-1 text-xs font-medium text-muted-foreground">
                        {calcText.oneTime}
                      </span>
                    </span>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>{t.requirementsLabel}</Label>
              <Textarea
                value={requirements}
                onChange={(e) => setRequirements(e.target.value)}
                placeholder={t.requirementsPlaceholder}
                rows={4}
                className="mt-1"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t.cancel}
              </Button>
              <Button
                onClick={() =>
                  onConfirm({
                    requirements: requirements.trim()
                      ? requirements.trim()
                      : undefined,
                  })
                }
                disabled={isPending}
                className="bg-brand-ink text-white hover:bg-brand-ink/90"
              >
                {isPending ? t.sending : t.step1Submit}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <DialogTitle className="font-display text-2xl font-extrabold leading-tight tracking-display sm:text-3xl">
                {t.step2Title}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                {t.step2Description}
              </DialogDescription>
            </DialogHeader>
            <CalendlyEmbed />
            <DialogFooter>
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-brand-ink text-white hover:bg-brand-ink/90"
              >
                {t.step2Done}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AskQuestionDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
  language,
  defaultEmail,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: { message: string; email?: string }) => void;
  isPending: boolean;
  language: ProposalLanguage;
  defaultEmail?: string;
}) {
  const [text, setText] = useState("");
  const [email, setEmail] = useState(defaultEmail ?? "");
  const t = getProposalTexts(language).questionDialog;

  useEffect(() => {
    if (open) setEmail(defaultEmail ?? "");
  }, [open, defaultEmail]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold tracking-display">
            {t.title}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {t.description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t.questionLabel}</Label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t.questionPlaceholder}
              className="mt-1"
            />
          </div>
          <div>
            <Label>{t.emailLabel}</Label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.emailPlaceholder}
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t.cancel}
          </Button>
          <Button
            disabled={!text || isPending}
            onClick={() => {
              onSubmit({ message: text, email: email || undefined });
              setText("");
              setEmail("");
            }}
            className="bg-brand-ink text-white hover:bg-brand-ink/90"
          >
            {isPending ? t.sending : t.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
